import { Injectable, Output, EventEmitter, NgZone } from '@angular/core';
import { ToastController, MenuController, LoadingController, AlertController, Platform } from '@ionic/angular';
import { Observable } from 'rxjs';
import { Router, RoutesRecognized } from '@angular/router';
import { pairwise, filter, map } from 'rxjs/operators';
import { Storage } from '@ionic/storage';
import { Browser } from '@capacitor/browser';
import * as CryptoJS from 'crypto-js';
import { StatusBar } from '@awesome-cordova-plugins/status-bar/ngx';
import { ThemeDetection, ThemeDetectionResponse } from '@awesome-cordova-plugins/theme-detection';
import { Preferences } from '@capacitor/preferences';
import { NativeBiometric } from "@capgo/capacitor-native-biometric";
import { App } from '@capacitor/app';
import { format, add, parseISO, differenceInDays, differenceInMonths, differenceInYears } from "date-fns";
import { TranslateService } from '@ngx-translate/core';
import { MIMETypesEnum } from '../../../models/mimetype.enum';

@Injectable({
  providedIn: 'root'
})
export class UtilsService {

  @Output() signedAct = new EventEmitter<any>();
  @Output() signedOI = new EventEmitter<any>();
  @Output() attachedFile = new EventEmitter<any>();
  @Output() menuStatus = new EventEmitter<any>();
  @Output() updateSlide = new EventEmitter<any>();
  private _storage: Storage | null = null;

  constructor(
    private toastController: ToastController,
    private menu: MenuController,
    private storage: Storage,
    private loader: LoadingController,
    private alertCtrl: AlertController,
    private router: Router,
    private platform: Platform,
    private ngZone: NgZone,
    private statusBar: StatusBar,
    private translate: TranslateService,
  ) {
    this.initStorage();
   }

   async initStorage() {
    // If using, define drivers here: await this.storage.defineDriver(/*...*/);
    const storage = await this.storage.create();
    this._storage = storage;
  }

  async showMessage(message) {
    const toast = await this.toastController.create({
      message,
      position: 'bottom',
      duration: 2000,
      buttons: [
        {
          side: 'end',
          icon: 'close',
          handler: () => { }
        }
      ]
    });
    toast.present();
  }

  async presentLoading(message = (this.translate.instant('utils.Cargando'))) {
    const loadingIsPresent = await this.loader.getTop();
    if (!loadingIsPresent) {
      const loading = await this.loader.create({
        message
      });
      await loading.present();
      return loading;
    }
  }

  dismissLoading() {
    setTimeout(async () => {
      const loadingIsPresent = await this.loader.getTop();
      if (loadingIsPresent)
        this.ngZone.run(() => this.loader.dismiss())
    }, 500);
  }

  async openMenu() {
    let isOpen = await this.menu.isOpen();
    !isOpen ? this.menu.open() : this.menu.close();
  }


  getUserInitials(name: string, lastname: string) {
    return `${name[0]}${lastname[0]}`;
  }

  async saveInStorage(key, value) {
    if (this.platform.is('mobileweb') || this.platform.is('desktop')) {
      value = typeof value === 'string' ? value : JSON.stringify(value)
      localStorage.setItem(key, value);
    } else {
      if (!key.includes('failuresDownloadedFiles'))
        await Preferences.set({ key, value: JSON.stringify(value) });
    }
  }

  async getFromStorage(key) {
    if (this.platform.is('mobileweb') || this.platform.is('desktop')) {
      let value = null;
      try {
        value = JSON.parse(localStorage.getItem(key));
      } catch {
        value = localStorage.getItem(key);
      } finally {
        if (value != null || value != undefined)
          return value;
        else
          throw new Error(`${key} not found`)
      }
    } else {
      const { value } = await Preferences.get({ key });
      if (value)
        return JSON.parse(value)
      else
        throw new Error(`${key} not found`);
    }
  }

  async removeFromStorage(key) {
    if (this.platform.is('mobileweb') || this.platform.is('desktop'))
      localStorage.removeItem(key);
    else
      await Preferences.remove({ key })
  }

  async getStorageKeys() {
    if (this.platform.is('mobileweb') || this.platform.is('desktop'))
      return Object.keys(localStorage);
    else
      return await Preferences.keys()
  }

  async clearStorage() {
    if (this.platform.is('mobileweb') || this.platform.is('desktop'))
      localStorage.clear();
    else
      await Preferences.clear();
  }

  encryptData(data: string) {
    return CryptoJS.AES.encrypt(data, `ccloudPassword`).toString();
  }

  decryptData(data: string) {
    return CryptoJS.AES.decrypt(data, `ccloudPassword`).toString(CryptoJS.enc.Utf8);
  }

  getFileExtension(file) {
    const dotIndex = file.lastIndexOf('.');
    if (dotIndex === -1) 
      return 'png'; 
  
    const ext = file.substring(dotIndex + 1).toLowerCase();
    return ext;
  }

  /** nombre sin extensión. */
  getFileNameWithoutExtension(fileName: string): string {
    const dotIndex = fileName.lastIndexOf('.');
    if (dotIndex === -1) return fileName;
    return fileName.substring(0, dotIndex);
  }

  getFileNameFromS3(file): string {
    try {
      const s3Name = new URL(file.url).pathname.split('/').pop();
  
      if (!s3Name) return 'archivo.png';
  
      return s3Name.includes('.')
        ? s3Name
        : `${s3Name}.png`;
  
    } catch {
      const fallback = file.archivo || file.nombre || 'archivo.png';
  
      return fallback.includes('.')
        ? fallback
        : `${fallback}.png`;
    }
  }
  
  
  getImage(extension) {
    switch (extension) {
      case 'png':
      case 'jpg':
      case 'jpeg':
        return '';
      case 'pdf':
        return '../../../../assets/imgs/pdf.svg';
      case 'xlsx':
      case 'xls':
        return '../../../../assets/imgs/xls.svg';
      case 'pptx':
      case 'ppt':
        return '../../../../assets/imgs/ppt.svg';
      case 'docx':
      case 'doc':
        return '../../../../assets/imgs/doc.svg';
      case 'txt':
        return '../../../../assets/imgs/txt.svg';
      case 'mp4':
      case 'webm':
        return '../../../../assets/imgs/video.svg';
    }
  }

  async confirmLeave(): Promise<Boolean> {
    let resolveLeaving;
    const canLeave = new Promise<Boolean>(resolve => resolveLeaving = resolve);
    const isAlertPresented = await this.alertCtrl.getTop();
    if (!isAlertPresented) {
      const leaveAlert = await this.alertCtrl.create({
        header: this.translate.instant('utils.Cambios pendientes'),
        message: this.translate.instant('utils.Existen modificaciones sin guardar, ¿Descartar cambios?'),
        buttons: [
          {
            text: this.translate.instant('utils.Descartar'),
            handler: () => resolveLeaving(true)
          },
          {
            text: this.translate.instant('utils.Cancelar'),
            handler: () => resolveLeaving(false)
          }
        ]
      })
      await leaveAlert.present();
    }
    return canLeave;
  }

  previousRoute$(): Observable<string> {
    return this.router.events.pipe(
      filter(e => e instanceof RoutesRecognized),
      pairwise(),
      map((e: [RoutesRecognized, RoutesRecognized]) => e[0].url)
    );
  }


  changeDateSeparator(date) {
    return date.replace(/-/g, '/');
  }


  openCCloudPage(isOnline) {
    if (this.platform.is('mobileweb') || this.platform.is('desktop'))
      window.open('https://www.calidadcloud.com/', '_system');
    else {
      if (isOnline) {
        Browser.open({ url: 'https://www.calidadcloud.com/' })
      } else
        this.showMessage(this.translate.instant('utils.Sin conexión a internet'));
    }
  }

  getDaysBetweenDates(begin, end?) {
    if (end)
      end = new Date(end.replace(/-/g, "/")).getTime();
    else
      end = new Date().getTime();
    begin = new Date(begin.replace(/-/g, "/").replace('T', ' ')).getTime();

    return Math.round(((begin - end) / (1000 * 3600 * 24)));
  }

  finishAC() {
    this.signedAct.emit();
  }

  finishOI() {
    this.signedOI.emit();
  }

  attachFile(flawDate) {
    this.attachedFile.emit(flawDate);
  }

  getHoursAndMinutes(time) {
    const integerPart = Math.floor(time);
    const decimalPartInMinutes = parseFloat((time - integerPart).toFixed(2)) * 60;
    let formattedTime = integerPart === 0 ? '' : integerPart === 1 ? `${integerPart} hora` : `${integerPart} horas`;
    formattedTime += decimalPartInMinutes === 0 ? '' : ` ${decimalPartInMinutes} minutos`;
    return formattedTime;
  }

  getTextWithoutHtmlTags(text) {
    return text.replace(/<[^>]*>/g, ' ');
  }

  async showFingerprint(): Promise<void> {
    return new Promise(async (res, rej) => {
      try {
        await NativeBiometric.verifyIdentity({
          title: this.translate.instant('utils.Huella digital'),
          description: this.translate.instant('utils.Escanea tu huella para continuar'),
          negativeButtonText: this.translate.instant('utils.Cancelar')
        })
        res();
      } catch {
        rej();
      }
    })
  }

  async logOutHelper() {
    let company = null, platform = null, androidPermission = null, pushHasPermission = null, propertiesViewMode = null;
    let savedCredentialsForFingerprint = null, configured_fingerprint = null, theme = null;
    try {
      platform = await this.getFromStorage('platform');
    } catch { }
    try {
      androidPermission = await this.getFromStorage('androidNotificationPermission');
    } catch { }
    try {
      pushHasPermission = await this.getFromStorage('pushHasPermission');
    } catch { }
    try {
      company = await this.getFromStorage('company');
    } catch { }
    try {
      propertiesViewMode = await this.getFromStorage('propertiesViewMode');
    } catch { }
    try {
      savedCredentialsForFingerprint = await this.getFromStorage('savedCredentialsForFingerprint');
    } catch { }
    try {
      configured_fingerprint = await this.getFromStorage('configured_fingerprint');
    } catch { }
    try {
      theme = await this.getFromStorage('theme');
    } catch { }
    finally {
      this.clearStorage();
      this.router.navigate(['/login']);
      if (company)
        this.saveInStorage('company', company)
      if (platform)
        this.saveInStorage('platform', platform)
      if (this.platform.is('android') || this.platform.is('ios')) {
        this.saveInStorage('alreadyLaunch', true);
        this.saveInStorage('androidNotificationPermission', androidPermission);
        this.saveInStorage('pushHasPermission', pushHasPermission);
        this.saveInStorage('totalNotifications', 0);
        this.saveInStorage('propertiesViewMode', propertiesViewMode);
        this.saveInStorage('savedCredentialsForFingerprint', savedCredentialsForFingerprint);
        this.saveInStorage('configured_fingerprint', configured_fingerprint);
        this.saveInStorage('theme', theme);
      }
    }
  }
  checkBrowser() {
    let userAgentString = navigator.userAgent;

    let chromeAgent = userAgentString.indexOf("Chrome") > -1;

    let IExplorerAgent = userAgentString.indexOf("MSIE") > -1 || userAgentString.indexOf("rv:") > -1;

    let firefoxAgent = userAgentString.indexOf("Firefox") > -1;

    let safariAgent = userAgentString.indexOf("Safari") > -1;

    if ((chromeAgent) && (safariAgent))
      safariAgent = false;

    let operaAgent = userAgentString.indexOf("OP") > -1;

    if ((chromeAgent) && (operaAgent))
      chromeAgent = false;

    const allBrowsers = [{
      name: 'Google Chrome',
      isUse: chromeAgent
    }, {
      name: 'Internet Explorer',
      isUse: IExplorerAgent
    }, {
      name: 'Firefox',
      isUse: firefoxAgent
    }, {
      name: 'Safari',
      isUse: safariAgent
    }, {
      name: 'Opera',
      isUse: operaAgent
    }];

    return allBrowsers.find(el => el.isUse).name;

  }

  checkOs() {
    if (navigator.userAgent.indexOf("Win") != -1)
      return "Windows OS";
    if (navigator.userAgent.indexOf("Mac") != -1)
      return "Macintosh";
    if (navigator.userAgent.indexOf("Linux") != -1)
      return "Linux OS";
    if (navigator.userAgent.indexOf("Android") != -1)
      return "Android OS";
    if (navigator.userAgent.indexOf("like Mac") != -1)
      return "iOS";

    return "Unknown OS";

  }


  findLastIndex<T>(array: Array<T>, predicate: (value: T, index: number, obj: T[]) => boolean): number {
    let l = array.length;
    while (l--) {
      if (predicate(array[l], l, array))
        return l;
    }
    return -1;
  }

  validateRut(rut) {
    rut = rut.substring(0, rut.length - 1) + "-" + rut.substring(rut.length - 1, rut.length);

    const tmp = rut.split('-');
    let digv = tmp[1];
    const fullRut = tmp[0];
    if (digv == 'K') digv = 'k';
    return (this.dv(fullRut) == digv);
  }

  dv(T) {
    let M = 0, S = 1;
    for (; T; T = Math.floor(T / 10))
      S = (S + T % 10 * (9 - M++ % 6)) % 11;
    return S ? S - 1 : 'k';
  }


  //dark theme
  initDarkThemeDetection() {
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)');
    prefersDark.addListener((mediaQuery) => this.toggleDarkTheme(mediaQuery.matches));
    ThemeDetection.isAvailable()
      .then((res: ThemeDetectionResponse) => {
        if (res.value) {
          ThemeDetection.isDarkModeEnabled().then((res: ThemeDetectionResponse) => {
            this.toggleDarkTheme(res.value)
          })
            .catch((error: any) => console.error(error));
        }
      })
      .catch((error: any) => console.error(error));
  }

  async toggleDarkTheme(shouldAdd) {
    if (this.platform.is('ios') && shouldAdd) {
      document.body.classList.toggle('dark', shouldAdd);
      this.statusBar.styleLightContent();
    } else if (this.platform.is('android') && shouldAdd) {
      document.body.classList.toggle('dark', shouldAdd);
      this.statusBar.styleDefault();
    } else if (!shouldAdd) {
      document.body.classList.toggle('dark', false);
      this.statusBar.styleDefault();
    }
    await this.saveInStorage('theme', document.body.getAttribute('class'));
  }

  ifNoNavParamRedirect(error) {
    if ((this.platform.is('mobileweb') || this.platform.is('desktop')) && error.message && error.message.includes('not found'))
      this.router.navigate(['/properties']);
  }

  async getAppInfo() {
    return await App.getInfo();
  }

  addTimeToDate(beginningDate, monthsToAdd) {
    return add(new Date(parseISO(format(beginningDate, 'yyyy-MM-dd'))), { months: monthsToAdd });
  }

  getValidity(validity) {
    const years = Math.trunc(validity / 12);
    const months = validity % 12;
    const monthsText = `${months > 1 ? this.translate.instant('utils.meses') : this.translate.instant('utils.mes')}`;
    const yearsText = `${years > 1 ? this.translate.instant('utils.años') : this.translate.instant('utils.año')}`;
    if (years && months)
      return `${years} ${yearsText} y ${months} ${monthsText}`;
    else if (years)
      return `${years} ${yearsText}`;
    else if (months)
      return `${months} ${monthsText}`;

    return ``;
  }


  getTimeDiffWarranty(dateLast, dateFirst) {
    if (dateFirst.getTime() > dateLast)
      return this.translate.instant('utils.Vencida');

    const days = differenceInDays(dateLast, dateFirst)
    const months = differenceInMonths(dateLast, dateFirst)
    const years = differenceInYears(dateLast, dateFirst)

    let textDays = days === 0 ? this.translate.instant('utils.último día') : (days === 1 ? `${days} ${this.translate.instant('utils.día')}` : `${days} ${this.translate.instant('utils.días')}`)
    let textMonths = months === 1 ? `${months} ${this.translate.instant('utils.mes')}` : `${months} ${this.translate.instant('utils.meses')}`
    let textYears = years === 1 ? `${years} ${this.translate.instant('utils.año')}` : `${years} ${this.translate.instant('utils.años')}`

    // recalcular días restando primero los meses
    const vigencia = add(new Date(format(dateLast, 'yyyy-MM-dd')), { months: -months })
    const divDays = (differenceInDays(parseISO(format(vigencia, 'yyyy-MM-dd')), dateFirst)) + 1
    const divMonths = months % 12

    if (divDays > 0) {
      textDays = divDays === 1 ? `${divDays} ${this.translate.instant('utils.día')}` : `${divDays} ${this.translate.instant('utils.días')}`
      textMonths = `${textMonths}, ${textDays}`
    }

    if (divMonths > 0 && months >= 12) {
      textMonths = divMonths === 1 ? `${divMonths} ${this.translate.instant('utils.mes')}` : `${divMonths} ${this.translate.instant('utils.meses')}`
      textYears = `${textYears}, ${textMonths}`
    }

    if (days <= 30) return textDays
    if (months < 12) return textMonths
    return textYears
  }

  async getLenguage(){
    return Preferences.get({key:'lenguage'});
  }

  async setLenguage(lenguage: string){
    await Preferences.set({
      key: 'lenguage',
      value: lenguage,
    });
    this.translate.use(lenguage);
  }

  async getCulture(){
    let culture = await this.getLenguage();
    return culture.value.includes('pt')? 'pt-BR' : 'es-CL';
  }
}
